"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { type CookieConsent, getConsentFromCookie, setConsentCookie } from "@/lib/cookies";
interface CookieConsentContextValue {
  consent: CookieConsent | null;
  hasConsented: boolean;
  showBanner: boolean;
  showPreferences: boolean;
  acceptAll: () => void;
  savePreferences: (analytics: boolean, marketing: boolean) => void;
  openPreferences: () => void;
  closePreferences: () => void;
}
const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);
export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return context;
}
export function CookieConsentProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const existingConsent = getConsentFromCookie();
      if (existingConsent) {
        setConsent(existingConsent);
        setShowBanner(false);
      } else {
        setShowBanner(true);
      }
      setIsInitialized(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = newConsent => {
      setConsentCookie(newConsent);
      setConsent(newConsent);
      setShowBanner(false);
      setShowPreferences(false);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const saveConsent = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      saveConsent({
        necessary: true,
        analytics: true,
        marketing: true,
        timestamp: new Date().toISOString()
      });
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const acceptAll = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (analytics, marketing) => {
      saveConsent({
        necessary: true,
        analytics,
        marketing,
        timestamp: new Date().toISOString()
      });
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const savePreferences = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setShowPreferences(true);
      setShowBanner(false);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const openPreferences = t6;
  let t7;
  if ($[6] !== consent) {
    t7 = () => {
      setShowPreferences(false);
      if (!consent) {
        setShowBanner(true);
      }
    };
    $[6] = consent;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const closePreferences = t7;
  const t8 = consent !== null;
  const t9 = isInitialized && showBanner;
  let t10;
  if ($[8] !== closePreferences || $[9] !== consent || $[10] !== showPreferences || $[11] !== t8 || $[12] !== t9) {
    t10 = {
      consent,
      hasConsented: t8,
      showBanner: t9,
      showPreferences,
      acceptAll,
      savePreferences,
      openPreferences,
      closePreferences
    };
    $[8] = closePreferences;
    $[9] = consent;
    $[10] = showPreferences;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const value = t10;
  let t11;
  if ($[14] !== children || $[15] !== value) {
    t11 = <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
    $[14] = children;
    $[15] = value;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}